import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Leaf } from "lucide-react";
import { CONTENT } from "@/content";
import { scrollToSection } from "@/components/Navbar";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskedLine = ({ index, children, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="hero" ref={ref} className="grain relative overflow-hidden bg-ivory" data-testid="hero-section">
      <div aria-hidden="true" className="wash wash-sage-light blob -left-[12%] -top-[18%] h-[560px] w-[560px]" />
      <div aria-hidden="true" className="wash wash-beige blob-alt -bottom-[12%] -right-[10%] h-[520px] w-[520px]" style={{ animationDelay: "-9s" }} />
      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-24 pt-36 lg:grid-cols-12 lg:px-12 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="mb-8 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.28em] text-gold"
            data-testid="hero-overline"
          >
            <span className="inline-block h-px w-10 bg-gold/60" />
            {CONTENT.name} — {CONTENT.title}
          </motion.p>

          <h1 className="font-serif text-5xl font-light leading-[1.08] tracking-tight text-anthracite sm:text-6xl lg:text-7xl" data-testid="hero-title">
            <MaskedLine index={0}>Remettre le corps</MaskedLine>
            <MaskedLine index={1}>
              en <em className="text-sage-dark">mouvement</em>,
            </MaskedLine>
            <MaskedLine index={2}>naturellement.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-md text-base font-light leading-relaxed tracking-wide text-taupe md:text-lg"
            data-testid="hero-subtitle"
          >
            Une ostéopathie douce, précise et pédagogique à <strong className="font-medium text-anthracite">Cabriès, Pays d'Aix</strong> — pour relâcher les
            tensions, comprendre son corps et retrouver durablement l'équilibre.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-52 w-full max-w-[340px]"
          >
            <div className="relative w-full" data-testid="hero-cta-booking-wrapper">
              <div className="flex flex-col gap-5">
                <a
                  data-testid="hero-booking-button"
                  href={CONTENT.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-shine block rounded-md bg-sage px-9 py-5 text-left text-sm font-medium uppercase tracking-[0.14em] text-ivory transition-colors duration-300 hover:bg-sage-dark"
                >
                  Prendre rendez-vous
                </a>
                <button
                  data-testid="hero-approach-button"
                  onClick={() => scrollToSection("#approche")}
                  className="block rounded-md border border-sage px-9 py-5 text-left text-sm font-medium uppercase tracking-[0.14em] text-taupe transition-colors duration-300 hover:bg-ivory"
                >
                  Découvrir mon approche
                </button>
              </div>
              <motion.img
                src="/mascotte-assise.png"
                alt=""
                aria-hidden="true"
                data-testid="hero-mascotte-assise"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
                className="pointer-events-none absolute -right-16 bottom-[60px] h-[272px] w-auto"
              />
            </div>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5" data-testid="hero-visual">
          <motion.div style={{ y: blobY }} className="absolute -left-8 -top-8 h-full w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="blob h-full w-full bg-sage-pale"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.img
              style={{ y: imgY }}
              src={CONTENT.images.hero}
              alt={CONTENT.images.heroAlt}
              className="soft-shadow relative aspect-[4/3] w-full scale-[1.04] rounded-xl object-cover"
            />
            <div className="absolute -bottom-6 -left-6 flex items-center gap-3 rounded-lg border border-beige bg-ivory/95 px-5 py-4 backdrop-blur-sm">
              <Leaf className="h-5 w-5 text-sage" strokeWidth={1} />
              <p className="font-serif text-lg italic text-taupe">Ostéopathie douce &amp; pédagogique</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#approche")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-taupe/70 transition-colors hover:text-anthracite lg:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Défiler</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" strokeWidth={1} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
